/*
 * Forward is the one composer in its Forward preset (`composer.tsx`, docs/2026-10-23-unified-composer-design.md):
 * the same head, fields, body, files and foot as a reply, filled in differently. To, Cc and Bcc start
 * empty and take what the rep types; From and Subject are the route's (`compose-capabilities.ts`). The
 * rep writes above the forwarded email, which is quoted under the words with the clients' divider
 * and stays editable, as in any mail client. No "note", no "message to forward".
 */
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type RefObject,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import type { InboxMessage } from "../../src/inbox-v2/contract.ts";
import type { ForwardComposition, ForwardRecipients } from "../../src/inbox-v2/forward-contract.ts";
import { SendIcon } from "../icons.tsx";
import { Tray } from "../tray.tsx";
import { inboxApi } from "./api.ts";
import { fieldCapability } from "./compose-capabilities.ts";
import {
  type ComposeMode,
  ComposerFoot,
  ComposerHead,
  FieldRow,
  FormatSwitch,
  RevealField,
} from "./composer.tsx";
import {
  compositionKey,
  forwardDraftKey,
  type StaleForward as Staleness,
  useForwardDraft,
} from "./forward-draft.ts";
import { ForwardEditor } from "./forward-editor.tsx";
import { sourceWords } from "./message-actions.tsx";
import { parseRecipientText } from "./recipient-input.ts";
import { AttachButton, OriginalFileList, ReplyFileList } from "./reply-attachments.tsx";
import { paragraphs } from "./reply-box-link.ts";
import { SignatureTool, useReplySender } from "./reply-sender.tsx";
import { endsWithSignature, ownWords, withSignature } from "./reply-signature.ts";
import { claimedWords, forwardAudience, HoldRow, sendKind, Uncertain } from "./send-states.tsx";
import { type ReplyFiles, useReplyFiles } from "./use-reply-files.ts";
import { useReplySend } from "./use-reply-send.ts";

type Sending = ReturnType<typeof useReplySend>;

type Props = {
  workspaceId: string;
  planId: string;
  userId: string;
  mailbox: string | null;
  /** Where the conversation is in Bison: the way to check a forward nobody could confirm. */
  bisonUrl?: string | null;
  message: InboxMessage;
  /** The reply presets this message offers, for the head's mode menu; none, the head names Forward. */
  replyModes?: readonly ComposeMode[];
  onReply?: (all: boolean) => void;
  onClose: () => void;
  phone?: boolean;
};

/** The head of every forward state: the mode menu, the forwarded email, the fold on a desktop. */
function ForwardHead(props: Props) {
  return (
    <ComposerHead
      mode="forward"
      modes={[...(props.replyModes ?? []), "forward"]}
      onMode={(mode) => {
        if (mode !== "forward") props.onReply?.(mode === "reply-all");
      }}
      source={sourceWords(props.message)}
      onFold={props.phone ? null : props.onClose}
    />
  );
}

/**
 * The sheet on a desktop, the tray on a phone, with the foot where each keeps its actions. The head
 * heads the sheet; on a phone it is the tray's own header, so there is one header and one surface.
 */
function Shell(props: {
  phone: boolean;
  onClose: () => void;
  head: ReactNode;
  foot?: ReactNode;
  children: ReactNode;
}) {
  if (props.phone)
    return (
      <Tray open title="Forward" heading={props.head} onClose={props.onClose} footer={props.foot}>
        <section className="lcr-bar lcr-i2-dock lcr-i2-forward" aria-label="Forward">
          <div className="lcr-i2-tray-reply">{props.children}</div>
        </section>
      </Tray>
    );
  return (
    <section className="lcr-bar lcr-i2-dock lcr-i2-forward" aria-label="Forward">
      <div className="lcr-sheet lcr-i2-reply-sheet lcr-i2-forward-sheet">
        {props.head}
        {props.children}
        {props.foot}
      </div>
    </section>
  );
}

/** Another reply or forward of this conversation is under way: nothing here may start a second. */
function OtherSend({ sending }: { sending: Sending }) {
  const send = sending.status?.send;
  if (!send) return null;
  const kind = sendKind(send);
  return send.state === "uncertain" ? null : (
    <p role="status">
      Another {kind} is in progress in this conversation. Wait for it to finish before forwarding
      this email.
    </p>
  );
}

const FORWARD_REFUSALS: Record<string, string> = {
  provider_rejected:
    "Bison refused this forward. Check the email in Bison before trying a new forward.",
  settled_not_sent: "You checked Bison and this forward did not go out. You can forward it again.",
  original_file_duplicate:
    "Forward was not sent: it carried a copy of a file Email Bison attaches from the original email itself. Remove the copy and send again.",
};

function forwardRefusal(reason: string | null | undefined): string {
  return (
    (reason ? FORWARD_REFUSALS[reason] : undefined) ??
    "Forward was not sent. Check the conversation and workspace settings."
  );
}

/** What the send did, while it holds the composer: held, under way, unknown, or sent. */
function ForwardHeld(props: Props & { sending: Sending; onAgain: () => void }) {
  const { sending } = props;
  const send = sending.status?.send;
  const other = send?.forwardTo
    ? send.sourceMessageId && send.sourceMessageId !== props.message.id
    : true;
  if (send && other && ["queued", "claimed"].includes(send.state))
    return <OtherSend sending={sending} />;
  if (sending.unknown || send?.state === "uncertain")
    return (
      <Uncertain
        send={send}
        kind="forward"
        bisonUrl={props.bisonUrl ?? null}
        busy={sending.busy}
        onSettle={sending.settle}
      />
    );
  if (send?.state === "queued")
    return (
      <HoldRow
        send={send}
        name={send.forwardTo ?? ""}
        busy={sending.busy}
        onUndo={() => void sending.undo()}
        grab
      />
    );
  if (send?.state === "claimed")
    return <p role="status">{claimedWords(send, send.forwardTo ?? "")}</p>;
  if (send?.state === "sent")
    return (
      <>
        <p role="status">
          Forwarded to {forwardAudience(send)}.
          {send.deliveredSubject ? ` Subject: ${send.deliveredSubject}.` : ""}
          {send.filesConfirmed === false && " Its files could not be confirmed; check Bison."}
        </p>
        <button type="button" className="lcr-small-button" onClick={props.onAgain}>
          Forward again
        </button>
      </>
    );
  return null;
}

export function ForwardBox(props: Props) {
  const [afterSendId, setAfterSendId] = useState<string | null>(null);
  const sending = useReplySend(
    props.workspaceId,
    props.planId,
    props.message.id,
    "forward",
    afterSendId,
  );
  const composition = useQuery({
    queryKey: [
      "inbox-v2",
      props.workspaceId,
      "forward-composition",
      props.planId,
      props.message.id,
      afterSendId,
    ],
    queryFn: ({ signal }) =>
      inboxApi.forwardComposition(props.workspaceId, props.planId, props.message.id, signal),
    enabled: !sending.loading && !sending.statusFailed && !sending.held && !sending.unknown,
    retry: false,
  });
  const phone = props.phone === true;
  const error = sending.error ? (
    <div className="lcr-reply-notice" data-tone="warn" role="alert">
      <p>{sending.error}</p>
    </div>
  ) : null;
  if (sending.held || sending.unknown)
    return (
      <Shell phone={phone} onClose={props.onClose} head={<ForwardHead {...props} />}>
        <div className="lcr-i2-forward-held">
          <ForwardHeld
            {...props}
            sending={sending}
            onAgain={() => setAfterSendId(sending.status?.send?.id ?? null)}
          />
        </div>
        {error}
      </Shell>
    );
  if (composition.data && !sending.statusFailed && !composition.isError)
    return (
      <ForwardComposer
        key={afterSendId ?? "first"}
        {...props}
        afterSendId={afterSendId}
        composition={composition.data}
        checked={composition.isFetchedAfterMount}
        sending={sending}
        error={error}
      />
    );
  return (
    <Shell phone={phone} onClose={props.onClose} head={<ForwardHead {...props} />}>
      <div className="lcr-i2-forward-held">
        <ForwardPreparing sending={sending} composition={composition} />
      </div>
    </Shell>
  );
}

/** Before the composer: the email being read for forwarding, or why it could not be. */
function ForwardPreparing(props: {
  sending: Sending;
  composition: Pick<UseQueryResult<ForwardComposition>, "isError" | "refetch">;
}) {
  if (props.sending.statusFailed)
    return (
      <p role="alert">
        Forward status could not be checked.{" "}
        <button type="button" className="lcr-linklike" onClick={props.sending.check}>
          Check again
        </button>
      </p>
    );
  if (props.composition.isError)
    return (
      <p role="alert">
        This email could not be prepared for forwarding.{" "}
        <button
          type="button"
          className="lcr-linklike"
          onClick={() => void props.composition.refetch()}
        >
          Try again
        </button>
      </p>
    );
  return <p role="status">Preparing the email…</p>;
}

/**
 * The rep's files that share a name with the forwarded email's own. Bison attaches those itself, so
 * a copy would reach the recipient twice; an older build put one there by itself. The server refuses
 * such a forward too (`original_file_duplicate`).
 */
function copiedOriginals(files: ReplyFiles, originals: readonly string[]): string[] {
  const names = [
    ...files.files.map((file) => file.fileName),
    ...files.pending.map((file) => file.name),
  ];
  return [...new Set(names.filter((name) => originals.includes(name)))];
}

function CopiedOriginals(props: { names: readonly string[] }) {
  if (props.names.length === 0) return null;
  const listed = props.names.join(", ");
  return (
    <div className="lcr-reply-notice" data-tone="warn" role="alert">
      <p>
        Email Bison already attaches {listed} from the original email. Remove your copy, or this
        forward would carry it twice. A different file needs another name.
      </p>
    </div>
  );
}

type Draft = ReturnType<typeof useForwardDraft>["draft"];
type Change = ReturnType<typeof useForwardDraft>["change"];
/** What every part of the composer draws from: the draft, how to change it, and whether it may. */
type Fields = { id: string; draft: Draft; change: Change; frozen: boolean };
type ForwardSend = Parameters<Sending["send"]>[0];

function ForwardComposer(
  props: Props & {
    composition: ForwardComposition;
    /** The composition was read since this composer opened, not served from an earlier read. */
    checked: boolean;
    afterSendId: string | null;
    sending: Sending;
    error: ReactNode;
  },
) {
  const id = useId();
  const key = `${forwardDraftKey(props.userId, props.workspaceId, props.planId, props.message.id)}:${props.afterSendId ?? "first"}`;
  const { draft, fresh, change, saved, stale, current, refresh } = useForwardDraft(
    key,
    props.composition,
  );
  const sender = useReplySender(props.workspaceId, props.mailbox);
  const signature = sender?.signature?.plainText ?? null;
  useForwardSignature(fresh, signature, draft.note, change);
  const { sending } = props;
  const frozen = sending.busy || sending.held || sending.unknown;
  const files = useReplyFiles({
    workspaceId: props.workspaceId,
    planId: props.planId,
    messageId: props.message.id,
    unsentKey: key,
    enabled: !!sending.status && !frozen,
    mode: "forward",
  });
  const originals = draft.composition.source.message.attachments.map((file) => file.name);
  const copies = copiedOriginals(files, originals);
  const phone = props.phone === true;
  const to = useToFocus(phone);
  const [tried, setTried] = useState(false);
  const { parsed, invalid } = forwardRecipients(draft);
  // Said once the rep leaves a field or presses Send, never while they are still typing in one.
  const [left, setLeft] = useState(false);
  const wrong = invalid && (left || tried);
  // A stale composition is refused by the server, and an older one of the same source would send an
  // older forwarded email, so neither is ever offered as sendable here.
  const sendable = [
    props.checked,
    current,
    !sending.locked,
    files.ready,
    copies.length === 0,
    parsed.success,
    imagesReady(draft),
  ].every(Boolean);
  const submit = () => {
    setTried(true);
    if (!sendable || !parsed.success || !draft.text.trim()) return;
    void sending.send(forwardRequest(draft, parsed.data, props.message.id, files.reference()));
  };
  const fields = { id, draft, change, frozen };
  const foot = (
    <ComposerFoot
      format={
        <FormatSwitch
          value={draft.format}
          disabled={frozen}
          onChange={(format) => change({ format })}
        />
      }
      tools={
        <SignatureTool
          sender={sender}
          signed={endsWithSignature(draft.note, signature)}
          onAdd={() => signature && change({ note: withSignature(draft.note, signature) })}
          onRemove={() => change({ note: ownWords(draft.note, signature) })}
          frozen={frozen}
          phone={phone}
        />
      }
      status={
        <p className="lcr-i2-reply-note" role="status">
          <ForwardStatus sending={sending} />
        </p>
      }
      actions={
        <>
          <AttachButton files={files} disabled={frozen} />
          <button
            type="button"
            className="lcr-primary"
            disabled={!sendable || !draft.text.trim()}
            onClick={submit}
          >
            <SendIcon size={16} />
            Send
          </button>
        </>
      }
    />
  );
  return (
    <Shell phone={phone} onClose={props.onClose} head={<ForwardHead {...props} />} foot={foot}>
      {/* biome-ignore lint/a11y/noStaticElementInteractions: Cmd or Ctrl and Enter sends from any field, as in the reply box */}
      <div
        className="lcr-i2-compose"
        onKeyDown={(event) => {
          if (event.key === "Escape" && !phone) {
            event.preventDefault();
            props.onClose();
            return;
          }
          if (!sendKeyPressed(event)) return;
          event.preventDefault();
          submit();
        }}
      >
        <ForwardFields
          {...fields}
          wrong={wrong}
          to={to}
          mailbox={draft.composition.source.senderAddress}
          onLeave={setLeft}
        />
        {stale && (
          <StaleForward
            reason={stale}
            disabled={frozen}
            onRefresh={() => {
              refresh();
              // The button goes with the notice; the rep carries on from their words.
              document.getElementById(`${id}-note`)?.focus();
            }}
          />
        )}
        {wrong && (
          <div className="lcr-reply-notice" data-tone="warn" role="alert" id={`${id}-invalid`}>
            <p>Enter valid email addresses, with each address in only one field.</p>
          </div>
        )}
        <ForwardBody {...fields} signature={signature} />
        <ImageNotices {...fields} />
        <OriginalFileList names={originals} capability={fieldCapability("forward", "originals")} />
        <CopiedOriginals names={copies} />
        <ReplyFileList files={files} frozen={frozen} />
        {!saved && (
          <div className="lcr-reply-notice" data-tone="warn" role="alert">
            <p>Your forward could not be saved in this browser. Keep it open until you send it.</p>
          </div>
        )}
        {props.error}
      </div>
    </Shell>
  );
}

/** The last thing a forward from this composer did, when that is news. */
function ForwardStatus({ sending }: { sending: Sending }) {
  const send = sending.status?.send;
  if (send?.state === "cancelled") return <>Forward undone. Your text is back.</>;
  if (send?.state === "refused") return <>{forwardRefusal(send.reason)}</>;
  return null;
}

/** The recipients parsed as the server parses them; wrong only once something is typed in To. */
function forwardRecipients(draft: Draft) {
  const parsed = parseRecipientText(draft);
  return { parsed, invalid: !parsed.success && draft.to !== "" };
}

/** Plain text, or formatted with no unsupported image left and any omitted ones confirmed. */
function imagesReady(draft: Draft): boolean {
  if (draft.format === "text") return true;
  return !draft.unsupportedImages && (draft.composition.omittedImages === 0 || draft.omitImages);
}

/** The forward request: the rep's words above the forwarded email, bound to what it was written on. */
function forwardRequest(
  draft: Draft,
  recipients: ForwardRecipients,
  messageId: string,
  selected: ReturnType<ReplyFiles["reference"]>,
): ForwardSend {
  return {
    operationId: crypto.randomUUID(),
    messageId,
    forwardTo: recipients.to[0] ?? "",
    forward: {
      recipients,
      sourceFingerprint: draft.composition.source.fingerprint,
      localFingerprint: draft.composition.localFingerprint,
    },
    simulation: false,
    format: draft.format,
    // The rep's words, when there are any, then the forwarded body exactly as the server composed it.
    html: `${draft.note.trim() ? paragraphs(draft.note) : ""}${draft.html}`,
    plainText: [draft.note.trim(), draft.text].filter(Boolean).join("\n\n"),
    ...(selected ? { files: selected } : {}),
  };
}

/** As in the reply box: Cmd or Ctrl and Enter sends from anywhere in it. */
function sendKeyPressed(event: ReactKeyboardEvent): boolean {
  return (
    event.key === "Enter" && (event.metaKey || event.ctrlKey) && !event.nativeEvent.isComposing
  );
}

/**
 * To takes the focus on a desktop, as a forward in any client opens: the addressee is the one thing
 * it cannot go without. Returns the To field's ref.
 */
function useToFocus(phone: boolean): RefObject<HTMLInputElement | null> {
  const to = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!phone) to.current?.focus({ preventScroll: true });
  }, [phone]);
  return to;
}

/** To, Cc and Bcc as the rep types them, Cc and Bcc behind a quiet link; From and Subject fixed. */
function ForwardFields(
  props: Fields & {
    wrong: boolean;
    to: RefObject<HTMLInputElement | null>;
    mailbox: string;
    /** True when the rep leaves a field, false while they type in one. */
    onLeave: (left: boolean) => void;
  },
) {
  const { id, draft, change, frozen, wrong } = props;
  const [shown, setShown] = useState({ cc: draft.cc !== "", bcc: draft.bcc !== "" });
  const [focus, setFocus] = useState<"cc" | "bcc" | null>(null);
  useEffect(() => {
    if (focus) document.getElementById(`${id}-${focus}`)?.focus();
  }, [focus, id]);
  const input = (field: "to" | "cc" | "bcc") => (
    <input
      ref={field === "to" ? props.to : undefined}
      id={`${id}-${field}`}
      className="lcr-i2-field-input"
      type="email"
      multiple
      required={field === "to"}
      value={draft[field]}
      disabled={frozen}
      autoComplete="off"
      maxLength={13000}
      aria-invalid={wrong && draft[field] !== "" ? true : undefined}
      aria-describedby={wrong ? `${id}-invalid` : undefined}
      onBlur={(event) => {
        props.onLeave(true);
        // A Cc or Bcc line left empty folds back to its quiet link, as it started.
        if (field !== "to" && !event.currentTarget.value.trim()) {
          setFocus(null);
          setShown((current) => ({ ...current, [field]: false }));
        }
      }}
      onChange={(event) => {
        props.onLeave(false);
        change({ [field]: event.currentTarget.value });
      }}
    />
  );
  const reveal = (field: "cc" | "bcc") => {
    setShown({ ...shown, [field]: true });
    setFocus(field);
  };
  return (
    <div className="lcr-i2-fields">
      <FieldRow
        label="To"
        inputId={`${id}-to`}
        capability={fieldCapability("forward", "to")}
        end={
          <>
            {!shown.cc && (
              <RevealField label="Cc" disabled={frozen} onReveal={() => reveal("cc")} />
            )}
            {!shown.bcc && (
              <RevealField label="Bcc" disabled={frozen} onReveal={() => reveal("bcc")} />
            )}
          </>
        }
      >
        {input("to")}
      </FieldRow>
      {shown.cc && (
        <FieldRow label="Cc" inputId={`${id}-cc`} capability={fieldCapability("forward", "cc")}>
          {input("cc")}
        </FieldRow>
      )}
      {shown.bcc && (
        <FieldRow label="Bcc" inputId={`${id}-bcc`} capability={fieldCapability("forward", "bcc")}>
          {input("bcc")}
        </FieldRow>
      )}
      <FieldRow
        label="From"
        capability={fieldCapability("forward", "from")}
        value={props.mailbox}
      />
      <FieldRow
        label="Subject"
        capability={fieldCapability("forward", "subject")}
        value={draft.composition.source.message.subject}
      />
    </div>
  );
}

/**
 * A forward that starts afresh holds the mailbox's signature in its words, once, as a reply does
 * (docs/2026-11-03-reply-box-mailbox-signature.md); a forward kept from before is left as it was.
 */
function useForwardSignature(
  fresh: boolean,
  signature: string | null,
  note: string,
  change: (patch: { note: string }) => void,
) {
  const signed = useRef(!fresh);
  useEffect(() => {
    if (signed.current || signature === null) return;
    signed.current = true;
    if (note.trim() === "") change({ note: withSignature("", signature) });
  }, [signature, note, change]);
}

/** The rep's words, then the forwarded email under its divider, in one scroll: as in any client. */
function ForwardBody({
  id,
  draft,
  change,
  frozen,
  signature,
}: Fields & { signature: string | null }) {
  return (
    <div className="lcr-i2-forward-body">
      <textarea
        id={`${id}-note`}
        className="lcr-i2-plain lcr-i2-forward-note"
        aria-label="Your message"
        placeholder="Write a message"
        rows={3}
        maxLength={50000}
        value={draft.note}
        disabled={frozen}
        // Holding only the signature, the words go above it, wherever the note was entered.
        onFocus={(event) => {
          if (ownWords(event.currentTarget.value, signature) === "")
            event.currentTarget.setSelectionRange(0, 0);
        }}
        onChange={(event) => change({ note: event.currentTarget.value })}
      />
      {draft.format === "html" ? (
        <ForwardEditor
          // The editor holds its own copy of the body; a refreshed or recomposed one starts anew.
          key={compositionKey(draft.composition)}
          html={draft.html}
          disabled={frozen}
          onChange={(html, text, unsupportedImages) => change({ html, text, unsupportedImages })}
        />
      ) : (
        <textarea
          aria-label="Forwarded email"
          className="lcr-i2-forward-preview"
          rows={8}
          value={draft.text}
          disabled={frozen}
          onChange={(event) => change({ text: event.currentTarget.value })}
        />
      )}
    </div>
  );
}

/** Formatted only: images that cannot go, and the choice to send without the omitted ones. */
function ImageNotices({ draft, change, frozen }: Fields) {
  if (draft.format !== "html") return null;
  return (
    <>
      {draft.unsupportedImages && (
        <div className="lcr-reply-notice" data-tone="warn" role="alert">
          <p>Some pasted images cannot be forwarded. Remove them or choose plain text.</p>
        </div>
      )}
      {draft.composition.omittedImages > 0 && (
        <div className="lcr-reply-notice" data-tone="info">
          <label className="lcr-i2-forward-check">
            <input
              type="checkbox"
              checked={draft.omitImages}
              disabled={frozen}
              onChange={(event) => change({ omitImages: event.currentTarget.checked })}
            />
            Send without {draft.composition.omittedImages} remote or unsupported images, or choose
            plain text.
          </label>
        </div>
      )}
    </>
  );
}

const STALE_WORDS: Record<NonNullable<Staleness>, string> = {
  source: "The original email changed since this forward was saved.",
  composition: "LeadCue now prepares this forwarded email differently than when it was saved.",
};

/**
 * The saved forward was composed from an earlier reading of its email, or by an earlier LeadCue
 * from an edited one: refresh before sending.
 */
function StaleForward(props: {
  reason: NonNullable<Staleness>;
  onRefresh: () => void;
  disabled: boolean;
}) {
  return (
    <div className="lcr-reply-notice" data-tone="warn" role="alert">
      <p>
        {STALE_WORDS[props.reason]} Refresh it to send: your words and recipients stay, and edits to
        the forwarded email are replaced.
      </p>
      <button
        type="button"
        className="lcr-small-button"
        disabled={props.disabled}
        onClick={props.onRefresh}
      >
        Refresh
      </button>
    </div>
  );
}
